import { useEffect, useMemo, useRef, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { supabase, callEdgeFunction } from "../lib/supabaseClient";
import { formatClicks, formatEuroFromCents, parseEuroInputToCents } from "../lib/format";
import { flagEmoji } from "../lib/flag";
import { useCountryClickStats } from "../hooks/useCountryClickStats";
import { useLanguage } from "../i18n/LanguageContext";
import { paypalLocale } from "../i18n/paypalLocale";
import type { TranslationKey } from "../i18n/types";
import type {
  AppLimits,
  CapturePayPalOrderResponse,
  CreatePayPalOrderResponse,
  CreateStripeCheckoutResponse,
  CreateSupportIntentResponse,
  OrderStatusResult,
  PaymentProvider,
  ProviderLimits,
} from "../lib/types";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

const bmcUsername = import.meta.env.VITE_BMC_USERNAME as string | undefined;
const paypalClientId = import.meta.env.VITE_PAYPAL_CLIENT_ID as string | undefined;

const PROVIDER_ORDER: PaymentProvider[] = ["stripe", "paypal", "buymeacoffee"];
const PROVIDER_LABEL_KEYS: Record<PaymentProvider, TranslationKey> = {
  stripe: "provider.stripe",
  paypal: "provider.paypal",
  buymeacoffee: "provider.buymeacoffee",
};

export interface PurchasePanelProps {
  countryNumeric: number;
  countryName: string;
  /** ISO 3166-1 alpha-2 code (e.g. "SI") - used only to prefix the Share text with a flag emoji, see lib/flag.ts. */
  countryAlpha2?: string;
  /** This country's 1-based position in the combined leaderboard, or null if it currently has zero clicks (unranked) - see MapPage's `selectedRank`. Feeds the Share button's text. */
  rank: number | null;
  /** Free (clicked) votes this country has collected so far - see MapPage's `freeRows`. */
  currentFreeClicks: number;
  currentPremiumClicks: number;
  currentAmountCents: number;
  limits: AppLimits;
  providers: Map<PaymentProvider, ProviderLimits>;
  /**
   * Set by MapPage when the visitor is redirected back from Stripe
   * Checkout (?stripe_order_id=... in the URL) - resumes straight into the
   * "awaiting confirmation" phase, polling by order id instead of starting
   * a brand new purchase.
   */
  resumeOrderId?: string;
  resumeCancelled?: boolean;
}

type Phase =
  | "choosing"
  | "creating"
  | "awaiting-payment"
  | "paypal-checkout"
  | "completed"
  | "cancelled"
  | "timed-out"
  | "error";

interface OrderIdentifier {
  referenceCode?: string;
  orderId?: string;
}

export function PurchasePanel({
  countryNumeric,
  countryName,
  countryAlpha2,
  rank,
  currentFreeClicks,
  currentPremiumClicks,
  currentAmountCents,
  limits,
  providers,
  resumeOrderId,
  resumeCancelled,
}: PurchasePanelProps) {
  const { t, language } = useLanguage();
  const enabledProviders = useMemo(
    () => PROVIDER_ORDER.filter((p) => providers.get(p)?.enabled),
    [providers],
  );

  const [provider, setProvider] = useState<PaymentProvider | null>(enabledProviders[0] ?? null);
  const [amountEur, setAmountEur] = useState("10");
  const [phase, setPhase] = useState<Phase>(resumeCancelled ? "cancelled" : "choosing");
  const [intent, setIntent] = useState<CreateSupportIntentResponse | null>(null);
  const [confirmedStatus, setConfirmedStatus] = useState<OrderStatusResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<"copied" | "shared" | null>(null);

  // Re-fetches whenever currentFreeClicks/currentPremiumClicks change (both
  // already realtime via MapPage's useCountryClicks), so the breakdown below
  // stays current for every visitor's click on this country, not just a
  // one-time snapshot from when it was first selected - see the hook.
  const { stats, loading: statsLoading } = useCountryClickStats(
    countryNumeric,
    `${currentFreeClicks}:${currentPremiumClicks}`,
  );

  // Pick a sensible default provider once the list of enabled providers has
  // loaded, without stomping on a choice the visitor already made.
  useEffect(() => {
    if (!provider && enabledProviders.length > 0) {
      setProvider(enabledProviders[0]);
    }
  }, [enabledProviders, provider]);

  // Reset all local state whenever the visitor selects a different country.
  useEffect(() => {
    setAmountEur("10");
    setPhase("choosing");
    setIntent(null);
    setConfirmedStatus(null);
    setErrorMessage(null);
    setCopied(false);
    setShareFeedback(null);
  }, [countryNumeric]);

  // Resume a Stripe redirect-return: jump straight into polling by order id.
  useEffect(() => {
    if (resumeOrderId) {
      setPhase("awaiting-payment");
      startPolling({ orderId: resumeOrderId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeOrderId]);

  const providerLimits = provider ? providers.get(provider) : undefined;
  const amountCents = useMemo(() => parseEuroInputToCents(amountEur), [amountEur]);
  const amountValid =
    !!providerLimits &&
    amountCents !== null &&
    amountCents >= providerLimits.minAmountCents &&
    amountCents <= providerLimits.maxAmountCents;
  const estimatedClicks = amountCents !== null ? amountCents * limits.premiumClicksPerCent : 0;

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDeadline = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  async function pollOnce(identifier: OrderIdentifier) {
    const { data, error } = await supabase
      .rpc("get_order_status", {
        p_reference_code: identifier.referenceCode ?? null,
        p_order_id: identifier.orderId ?? null,
      })
      .single();
    if (error) {
      console.error("get_order_status failed", error);
      return;
    }
    const result = data as OrderStatusResult;
    if (result.status === "Completed") {
      setConfirmedStatus(result);
      setPhase("completed");
      stopPolling();
      return;
    }
    if (result.status === "Failed" || result.status === "Cancelled") {
      setPhase("cancelled");
      stopPolling();
      return;
    }
    if (Date.now() > pollDeadline.current) {
      setPhase("timed-out");
      stopPolling();
    }
  }

  const pollIdentifier = useRef<OrderIdentifier>({});

  function startPolling(identifier: OrderIdentifier) {
    stopPolling();
    pollIdentifier.current = identifier;
    pollDeadline.current = Date.now() + POLL_TIMEOUT_MS;
    void pollOnce(identifier); // check immediately - useful right after a redirect-return
    pollTimer.current = setInterval(() => {
      void pollOnce(identifier);
    }, POLL_INTERVAL_MS);
  }

  async function handleContinue() {
    if (!provider || !amountValid || amountCents === null) return;
    setErrorMessage(null);

    if (provider === "buymeacoffee") {
      setPhase("creating");
      try {
        const response = await callEdgeFunction<CreateSupportIntentResponse>("create-support-intent", {
          countryNumeric,
          requestedAmountCents: amountCents,
        });
        setIntent(response);
        setPhase("awaiting-payment");
        // Deliberately does NOT open the BuyMeCoffee tab here anymore - that
        // used to happen automatically at this exact point, which in
        // practice whisked the visitor away to buymeacoffee.com before this
        // component ever got to render the code/instructions below, so
        // nobody actually saw them. The visitor now sees the code and the
        // "paste this in your message" instruction first, and clicks the
        // explicit "Odpri BuyMeCoffee" button themselves when ready - see
        // the awaiting-payment render block further down.
        copyReferenceCode(response.referenceCode); // best-effort - already on their clipboard once they get there
        startPolling({ referenceCode: response.referenceCode });
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : t("panel.errorPreparingSupport"));
        setPhase("error");
      }
      return;
    }

    if (provider === "stripe") {
      setPhase("creating");
      try {
        const response = await callEdgeFunction<CreateStripeCheckoutResponse>("create-stripe-checkout", {
          countryNumeric,
          requestedAmountCents: amountCents,
          frontendOrigin: window.location.origin,
        });
        // Full-page redirect to Stripe's hosted Checkout - the visitor
        // returns to ?stripe_order_id=<orderId>, which MapPage reads
        // and passes back down as resumeOrderId.
        window.location.href = response.checkoutUrl;
      } catch (e) {
        setErrorMessage(e instanceof Error ? e.message : t("panel.errorPreparingPayment"));
        setPhase("error");
      }
      return;
    }

    // paypal: no network call here - just lock in the amount and reveal the
    // inline PayPalButtons below, which themselves drive
    // create-paypal-order (createOrder) / capture-paypal-order (onApprove).
    setPhase("paypal-checkout");
  }

  function copyReferenceCode(code: string) {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {}, // clipboard permission denied/unavailable - the code is still shown on screen, just not auto-copied
    );
  }

  function handleCopyCode() {
    if (!intent) return;
    copyReferenceCode(intent.referenceCode);
  }

  function handleOpenBuyMeCoffee() {
    if (!bmcUsername) return;
    window.open(`https://www.buymeacoffee.com/${bmcUsername}`, "_blank", "noopener,noreferrer");
  }

  /**
   * "Ko uporabnik klikne Share, ga lahko neposredno deli" - builds a short,
   * punchy, rank-aware message ("#3! Help us get to #1") so sharing this
   * country turns every visitor into a potential promoter of the app, per
   * the task. Prefers the native Web Share API (the share sheet on phones,
   * some desktop browsers) so the visitor picks WhatsApp/X/Messages/etc.
   * themselves; falls back to copying "text + link" to the clipboard where
   * navigator.share isn't available (most desktop browsers).
   */
  function buildShareText(): string {
    const flag = countryAlpha2 ? `${flagEmoji(countryAlpha2)} ` : "";
    if (rank === null) {
      return t("share.unranked", { flag, country: countryName });
    }
    if (rank === 1) {
      return t("share.rank1", { flag, country: countryName });
    }
    return t("share.rankN", { flag, country: countryName, rank });
  }

  async function handleShare() {
    const text = buildShareText();
    const url = `${window.location.origin}${window.location.pathname}`; // clean link, no stray query params

    if (navigator.share) {
      try {
        await navigator.share({ text, url });
        setShareFeedback("shared");
        setTimeout(() => setShareFeedback(null), 2000);
      } catch {
        // AbortError etc. - the visitor cancelled the native share sheet, not an error
      }
      return;
    }

    navigator.clipboard?.writeText(`${text}\n${url}`).then(
      () => {
        setShareFeedback("copied");
        setTimeout(() => setShareFeedback(null), 2000);
      },
      () => {},
    );
  }

  function handleCheckAgain() {
    setPhase("awaiting-payment");
    startPolling(pollIdentifier.current);
  }

  function handleBackToChoosing() {
    setErrorMessage(null);
    setPhase("choosing");
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{t("panel.selectedCountryLabel")}</p>
          <p className="text-lg font-semibold text-slate-900">{countryName}</p>
          {rank !== null && (
            <p className="mt-0.5 text-xs text-slate-500">{t("panel.currentlyRanked", { rank })}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleShare}
          className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          {shareFeedback === "copied"
            ? t("panel.shareCopied")
            : shareFeedback === "shared"
              ? t("panel.shareShared")
              : t("panel.shareButton")}
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500">
              <th className="px-3 py-1.5 text-left font-medium">{t("panel.tableClicks")}</th>
              <th className="px-3 py-1.5 text-right font-medium">{t("panel.tableFree")}</th>
              <th className="px-3 py-1.5 text-right font-medium">{t("panel.tablePaid")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-1.5 text-slate-500">{t("panel.rowAllTime")}</td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">
                {formatClicks(currentFreeClicks, language)}
              </td>
              <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">
                {formatClicks(currentPremiumClicks, language)}
              </td>
            </tr>
            <tr>
              <td className="px-3 py-1.5 text-slate-500">{t("panel.rowThisYear")}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.free_this_year, language)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.premium_this_year, language)}
              </td>
            </tr>
            <tr>
              <td className="px-3 py-1.5 text-slate-500">{t("panel.rowThisWeek")}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.free_this_week, language)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.premium_this_week, language)}
              </td>
            </tr>
            <tr>
              <td className="px-3 py-1.5 text-slate-500">{t("panel.rowToday")}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.free_today, language)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">
                {statsLoading || !stats ? "…" : formatClicks(stats.premium_today, language)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-sm text-slate-600">
        {t("panel.totalCombined")}{" "}
        <span className="font-semibold tabular-nums text-slate-900">
          {formatClicks(currentFreeClicks + currentPremiumClicks, language)}
        </span>
        {" · "}
        {t("panel.totalSupport")}{" "}
        <span className="font-semibold tabular-nums text-slate-900">
          {formatEuroFromCents(currentAmountCents, language)}
        </span>
      </p>

      {(phase === "choosing" || phase === "creating" || phase === "error") && (
        <>
          <div>
            <p className="mb-1.5 text-sm font-medium text-slate-700">{t("panel.paymentMethod")}</p>
            {enabledProviders.length === 0 ? (
              <p className="text-sm text-red-600">{t("panel.noPaymentMethod")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {enabledProviders.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      provider === p
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {t(PROVIDER_LABEL_KEYS[p])}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="amount" className="mb-1.5 block text-sm font-medium text-slate-700">
              {t("panel.supportAmount")}
            </label>
            <input
              id="amount"
              type="text"
              inputMode="decimal"
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-lg font-semibold tabular-nums focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              placeholder="10.00"
            />
            {!amountValid && amountEur !== "" && providerLimits && (
              <p className="mt-1 text-xs text-red-600">
                {t("panel.amountRange", {
                  min: formatEuroFromCents(providerLimits.minAmountCents, language),
                  max: formatEuroFromCents(providerLimits.maxAmountCents, language),
                })}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {limits.quickAmountsEur.map((eur) => (
              <button
                key={eur}
                type="button"
                onClick={() => setAmountEur(String(eur))}
                className={`rounded-full border px-3 py-1 text-sm font-medium transition ${
                  amountEur === String(eur)
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
              >
                {t("panel.quickAmount", { eur })}
              </button>
            ))}
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-900">
            <p>
              {t("panel.estimatedClicks")}{" "}
              <span className="font-semibold tabular-nums">{formatClicks(estimatedClicks, language)}</span>
            </p>
            <p className="mt-1 text-xs text-amber-800">
              {t("panel.ratioInfo", {
                a: formatEuroFromCents(1, language),
                b: formatClicks(limits.premiumClicksPerCent, language),
                c: formatEuroFromCents(100, language),
                d: formatClicks(100 * limits.premiumClicksPerCent, language),
              })}
            </p>
          </div>

          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

          <button
            type="button"
            onClick={handleContinue}
            disabled={!provider || !amountValid || phase === "creating" || (provider === "buymeacoffee" && !bmcUsername)}
            className="w-full rounded-lg bg-amber-500 px-4 py-2.5 font-semibold text-slate-900 shadow-sm transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {phase === "creating"
              ? t("panel.preparing")
              : provider
                ? t("panel.continueWith", { provider: t(PROVIDER_LABEL_KEYS[provider]) })
                : t("panel.choosePaymentMethod")}
          </button>
          {provider === "buymeacoffee" && !bmcUsername && (
            <p className="text-xs text-red-600">{t("panel.bmcUsernameMissing")}</p>
          )}
          {provider === "paypal" && !paypalClientId && (
            <p className="text-xs text-red-600">{t("panel.paypalClientIdMissing")}</p>
          )}
        </>
      )}

      {phase === "paypal-checkout" && amountCents !== null && (
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm text-slate-700">
            {t("panel.paypalAmountInfo", {
              amount: formatEuroFromCents(amountCents, language),
              clicks: formatClicks(estimatedClicks, language),
            })}
          </p>
          {!paypalClientId ? (
            <p className="text-sm text-red-600">{t("panel.paypalClientIdMissing")}</p>
          ) : (
            <PayPalScriptProvider
              options={{ clientId: paypalClientId, currency: "EUR", intent: "capture", locale: paypalLocale(language) }}
            >
              <PayPalButtons
                style={{ layout: "horizontal" }}
                createOrder={async () => {
                  setErrorMessage(null);
                  const response = await callEdgeFunction<CreatePayPalOrderResponse>("create-paypal-order", {
                    countryNumeric,
                    requestedAmountCents: amountCents,
                  });
                  return response.paypalOrderId;
                }}
                onApprove={async (data) => {
                  try {
                    const response = await callEdgeFunction<CapturePayPalOrderResponse>("capture-paypal-order", {
                      paypalOrderId: data.orderID,
                    });
                    if (response.order.status === "Completed") {
                      setConfirmedStatus(response.order);
                      setPhase("completed");
                    } else {
                      // e.g. capture is PENDING a manual review - keep the
                      // visitor informed and let paypal-webhook finish it.
                      setPhase("awaiting-payment");
                      startPolling({ orderId: response.orderId });
                    }
                  } catch (e) {
                    setErrorMessage(e instanceof Error ? e.message : t("panel.errorConfirmingPayment"));
                    setPhase("error");
                  }
                }}
                onCancel={() => setPhase("cancelled")}
                onError={(err) => {
                  console.error("PayPal button error", err);
                  setErrorMessage(t("panel.errorPayingPaypal"));
                  setPhase("error");
                }}
              />
            </PayPalScriptProvider>
          )}
          <button
            type="button"
            onClick={handleBackToChoosing}
            className="self-start text-sm font-medium text-slate-600 underline hover:text-slate-900"
          >
            {t("panel.back")}
          </button>
        </div>
      )}

      {(phase === "awaiting-payment" || phase === "timed-out") && (
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {intent && (
            <>
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                <p className="font-semibold">{t("panel.bmcWarningTitle")}</p>
                <p className="mt-1">{t("panel.bmcWarningBody")}</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md bg-white border border-slate-300 px-3 py-2 text-center text-lg font-mono font-bold tracking-wider text-slate-900">
                  {intent.referenceCode}
                </code>
                <button
                  type="button"
                  onClick={handleCopyCode}
                  className="shrink-0 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
                >
                  {copied ? t("panel.copied") : t("panel.copy")}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                {t("panel.suggestedAmount")}{" "}
                <span className="font-semibold tabular-nums text-slate-900">
                  {formatEuroFromCents(intent.requestedAmountCents, language)}
                </span>{" "}
                {t("panel.bmcAmountNote", { clicks: formatClicks(intent.estimatedPremiumClicks, language) })}
              </p>
              {bmcUsername ? (
                <button
                  type="button"
                  onClick={handleOpenBuyMeCoffee}
                  className="w-full rounded-lg bg-amber-500 px-4 py-2.5 font-semibold text-slate-900 shadow-sm transition hover:bg-amber-400"
                >
                  {t("panel.openBmc")}
                </button>
              ) : (
                <p className="text-xs text-red-600">{t("panel.bmcUsernameMissing")}</p>
              )}
            </>
          )}
          {!intent && <p className="text-sm text-slate-700">{t("panel.waitingProvider")}</p>}
          {phase === "awaiting-payment" && (
            <p className="text-sm text-slate-600 flex items-center gap-2">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
              {t("panel.waitingConfirmation")}
            </p>
          )}
          {phase === "timed-out" && (
            <div className="text-sm text-slate-600">
              <p>{t("panel.timedOut")}</p>
              <button
                type="button"
                onClick={handleCheckAgain}
                className="mt-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
              >
                {t("panel.checkAgain")}
              </button>
            </div>
          )}
        </div>
      )}

      {phase === "cancelled" && (
        <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <p>{t("panel.cancelled")}</p>
          <button
            type="button"
            onClick={handleBackToChoosing}
            className="self-start rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          >
            {t("panel.tryAgain")}
          </button>
        </div>
      )}

      {phase === "completed" && confirmedStatus && (
        <div className="flex flex-col gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-emerald-900">
          <p className="font-semibold">{t("panel.confirmed")}</p>
          <p className="text-sm">
            {t("panel.confirmedBody", {
              country: countryName,
              clicks: formatClicks(confirmedStatus.premium_clicks ?? 0, language),
              amount: formatEuroFromCents(confirmedStatus.amount_cents ?? 0, language),
            })}
          </p>
        </div>
      )}
    </div>
  );
}
